import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import axios from 'axios';
import type SignaturePadElement from './signature_pad_element';

/**
 * Lets a user upload an image or PDF of their signature instead of typing or drawing it.
 *
 * The chosen file is posted to `src`, which converts and trims it on the server and answers with
 * the PNG as a data URL. That data URL is shown as the preview and written into the same hidden
 * field the signature pad fills, so the form saves an upload exactly like a typed or drawn
 * signature. The file input itself has no name and is never submitted with the form.
 *
 * Used on the profile signature page (inside its Upload tab) and on onboarding step 4.
 */
@registerElement('signature-upload')
export default class SignatureUploadElement extends ImpulseElement {
  /**
   * The endpoint that converts the file (Users::SignaturesController#preview_upload).
   */
  @property() src: string;

  /**
   * The id of the hidden field that holds the signature data URL the form submits.
   */
  @property() field: string;

  /**
   * The id of the hidden field that records how the signature was made, if the form has one.
   */
  @property() sourceField = '';

  /**
   * A selector for parts of the surrounding signature pad to hide while an upload is shown (the
   * typing field and canvas on onboarding), so the pad cannot overwrite the uploaded image.
   */
  @property() conceal = '';

  /**
   * The largest file the converter accepts (Converter::SignatureUpload::MAX_FILE_SIZE), checked
   * here first so an oversized file is refused at once instead of after uploading it.
   */
  @property({ type: Number }) maxBytes = 0;

  /**
   * The converter's own refusal for a file over maxBytes (Converter::SignatureUpload::TOO_LARGE_MESSAGE).
   */
  @property() tooLargeMessage = '';

  @target() fileInput: HTMLInputElement;
  @target() preview: HTMLElement;
  @target() previewImage: HTMLImageElement;
  @target() attestation?: HTMLInputElement;
  @target() loading: HTMLElement;
  @target() error: HTMLElement;

  private padWasRequired = false;
  private sourceBeforeUpload = '';
  private uploaded = false;
  private clearingPad = false;
  private abortController?: AbortController;
  private request?: AbortController;

  connected() {
    this.abortController = new AbortController();
    // Clearing the pad (its Clear button, or switching tabs) also discards the upload.
    this.signaturePad?.addEventListener(
      'signature-pad:clear',
      () => {
        if (!this.clearingPad) this.reset();
      },
      { signal: this.abortController.signal }
    );
  }

  disconnected() {
    this.abortController?.abort();
  }

  async choose() {
    const file = this.fileInput.files?.[0];
    if (!file) {
      this.reset();
      return;
    }

    if (this.maxBytes && file.size > this.maxBytes) {
      this.reset();
      this.showError(this.tooLargeMessage || 'This file is too large to upload. Please choose a smaller file.');
      return;
    }

    const formData = new FormData();
    formData.append('signature_file', file);

    // A newer file, Clear or a tab switch supersedes this request, so its answer must not land.
    this.request?.abort();
    const request = new AbortController();
    this.request = request;

    this.error.hidden = true;
    this.loading.hidden = false;
    try {
      const { data } = await axios.post<{ data_url: string }>(this.src, formData, { signal: request.signal });
      if (request.signal.aborted) return;
      // A redirect to sign in (expired session or CSRF token) answers 200 with a page, not JSON.
      if (typeof data?.data_url !== 'string' || !data.data_url.startsWith('data:image/png;base64,')) {
        throw new Error('The conversion did not return a signature image.');
      }

      this.show(data.data_url);
    } catch (error) {
      if (request.signal.aborted) return;

      this.reset();
      this.showError(this.failureMessage(error));
    } finally {
      if (this.request === request) this.request = undefined;
      if (!this.request) this.loading.hidden = true;
    }
  }

  /**
   * Discards the upload and gives the signature back to the pad.
   */
  reset() {
    this.request?.abort();
    this.request = undefined;
    this.loading.hidden = true;
    this.fileInput.value = '';
    this.previewImage.removeAttribute('src');
    this.preview.hidden = true;
    this.error.hidden = true;
    if (this.attestation) {
      this.attestation.checked = false;
      this.attestation.required = false;
    }
    if (!this.uploaded) return;

    this.uploaded = false;
    this.setField(this.field, '');
    if (this.sourceField) this.setField(this.sourceField, this.sourceBeforeUpload);
    this.toggleConcealed(false);
    if (this.signaturePad) this.signaturePad.required = this.padWasRequired;
    this.emit('change', { detail: { uploaded: false } });
  }

  private show(dataUrl: string) {
    if (!this.uploaded) {
      // Start the pad over before hiding it. Otherwise discarding the upload later (a failed
      // re-upload) would bring back whatever was typed or drawn before, showing a signature the
      // hidden field no longer holds.
      this.clearingPad = true;
      this.signaturePad?.clear();
      this.clearingPad = false;
      this.padWasRequired = this.signaturePad?.required ?? false;
      this.sourceBeforeUpload = this.sourceField ? (this.fieldById(this.sourceField)?.value ?? '') : '';
    }
    this.uploaded = true;

    this.previewImage.src = dataUrl;
    this.preview.hidden = false;
    if (this.attestation) this.attestation.required = true;
    this.setField(this.field, dataUrl);
    if (this.sourceField) this.setField(this.sourceField, 'uploaded');
    this.toggleConcealed(true);
    // The pad's own "required" check looks at its typing field, which is empty after an upload.
    if (this.signaturePad) this.signaturePad.required = false;
    this.emit('change', { detail: { uploaded: true } });
  }

  private failureMessage(error: unknown): string {
    if (!axios.isAxiosError(error)) return 'We could not upload this file. Please try again.';
    // The web server refuses bodies over its own limit before the app sees them, with an HTML page.
    if (error.response?.status === 413) return 'This file is too large to upload. Please choose a smaller file.';

    const message = (error.response?.data as { error?: string } | undefined)?.error;
    return typeof message === 'string' && message ? message : 'We could not upload this file. Please try again.';
  }

  private showError(message: string) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  private setField(id: string, value: string) {
    const input = this.fieldById(id);
    if (!input) return;

    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private fieldById(id: string): HTMLInputElement | null {
    return document.getElementById(id) as HTMLInputElement | null;
  }

  private toggleConcealed(concealed: boolean) {
    if (!this.conceal || !this.signaturePad) return;

    this.signaturePad.querySelectorAll<HTMLElement>(this.conceal).forEach((element) => {
      element.hidden = concealed;
    });
    // The canvas cannot measure itself while hidden, so let it re-fit now that it is back.
    if (!concealed) window.dispatchEvent(new Event('resize'));
  }

  private get signaturePad(): SignaturePadElement | null {
    return this.closest('signature-pad');
  }
}
